import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, ArrowLeft, Plus, X, Save, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import ActivityEditor from "@/components/ActivityEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CreateCourse({ user }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [activities, setActivities] = useState("");
  const [expectations, setExpectations] = useState("");
  const [durationWeeks, setDurationWeeks] = useState(4);
  const [moduleId, setModuleId] = useState(undefined);
  const [modules, setModules] = useState([]);
  const [content, setContent] = useState([{ type: "text", title: "", content: "", url: "", dailyActivities: [], expanded: true }]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  const addLesson = () => {
    setContent([...content, { type: "text", title: "", content: "", url: "", dailyActivities: [], expanded: true }]);
  };

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      const response = await fetch(`${API}/modules`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setModules(data);
      }
    } catch (error) {
      console.error('Failed to load modules');
    }
  };

  const removeLesson = (index) => {
    setContent(content.filter((_, i) => i !== index));
  };

  const updateLesson = (index, field, value) => {
    const newContent = [...content];
    newContent[index][field] = value;
    setContent(newContent);
  };

  const updateLessonActivities = (index, activities) => {
    const newContent = [...content];
    newContent[index].dailyActivities = activities;
    setContent(newContent);
  };

  const toggleLessonExpand = (index) => {
    const newContent = [...content];
    newContent[index].expanded = !newContent[index].expanded;
    setContent(newContent);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const courseContent = content.map(item => {
      const lesson = { 
        type: item.type, 
        title: item.title,
        dailyActivities: item.dailyActivities || []
      };
      if (item.type === "text") {
        lesson.content = item.content;
      } else if (item.type === "video" || item.type === "link") {
        lesson.url = item.url;
      }
      return lesson;
    });

    try {
      const response = await fetch(`${API}/courses`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title,
          description,
          thumbnail: thumbnail || null,
          activities,
          expectations,
          duration_weeks: durationWeeks,
          module_id: moduleId || null,
          content: courseContent
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Course created successfully!');
        navigate(`/courses/${data.course_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create course');
      }
    } catch (error) {
      toast.error('Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'teacher') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <p className="text-[#588157] mb-4">Only teachers can create courses</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard-button"
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-4 sm:p-6 md:p-12">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-2xl sm:text-4xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>Create New Course</h1>
          </div>
          <p className="text-base sm:text-lg text-[#588157]">Share your knowledge and guide students in their faith journey</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          <Card className="p-4 sm:p-6 md:p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
            <h2 className="text-xl sm:text-2xl font-bold text-[#344E41] mb-4 sm:mb-6" style={{ fontFamily: 'Merriweather, serif' }}>Course Information</h2>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="title" className="text-[#344E41]">Course Title *</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  data-testid="course-title-input"
                  className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-[#344E41]">Description *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={3}
                  data-testid="course-description-input"
                  className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="thumbnail" className="text-[#344E41]">Thumbnail URL (Optional)</Label>
                <Input
                  id="thumbnail"
                  value={thumbnail}
                  onChange={(e) => setThumbnail(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  data-testid="course-thumbnail-input"
                  className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="activities" className="text-[#344E41]">Course Activities</Label>
                <Textarea
                  id="activities"
                  value={activities}
                  onChange={(e) => setActivities(e.target.value)}
                  placeholder="Describe the activities students will engage in..."
                  rows={3}
                  data-testid="course-activities-input"
                  className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="expectations" className="text-[#344E41]">Expectations</Label>
                <Textarea
                  id="expectations"
                  value={expectations}
                  onChange={(e) => setExpectations(e.target.value)}
                  placeholder="What are your expectations for students taking this course?"
                  rows={3}
                  data-testid="course-expectations-input"
                  className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                />
              </div>

              <div>
                <Label htmlFor="duration" className="text-[#344E41]">Duration (weeks) *</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  value={durationWeeks}
                  onChange={(e) => setDurationWeeks(parseInt(e.target.value))}
                  required
                  data-testid="course-duration-input"
                  className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="module" className="text-[#344E41]">Module (Optional)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open('/modules/create', '_blank')}
                    className="text-xs text-[#3A5A40] hover:bg-[#E9EDC9]"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Create New Module
                  </Button>
                </div>
                <Select value={moduleId || ""} onValueChange={(val) => setModuleId(val || null)}>
                  <SelectTrigger data-testid="course-module-select" className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] rounded-lg">
                    <SelectValue placeholder={modules.length > 0 ? "Select a module (optional)" : "No modules available - create one first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {modules.length === 0 ? (
                      <div className="p-4 text-center text-sm text-[#588157]">
                        <p>No modules yet</p>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => navigate('/modules/create')}
                          className="mt-2 bg-[#3A5A40] text-white hover:bg-[#344E41]"
                        >
                          Create First Module
                        </Button>
                      </div>
                    ) : (
                      modules.map((module) => (
                        <SelectItem key={module.module_id} value={module.module_id}>
                          {module.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#588157] mt-1">
                  {modules.length > 0 
                    ? 'Group this course under a module for better organization and certificate tracking'
                    : 'Create a module first to organize your courses into learning paths'
                  }
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 sm:p-6 md:p-8 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>Learning Materials</h2>
              <Button
                type="button"
                onClick={addLesson}
                data-testid="add-lesson-button"
                className="bg-[#D4A373] text-white hover:bg-[#C89563] duration-200 font-medium rounded-lg w-full sm:w-auto"
              >
                <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Add Lesson
              </Button>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {content.map((lesson, index) => (
                <Card key={index} className="p-4 sm:p-6 bg-white/50 border-[#CCD5AE] rounded-xl" data-testid={`lesson-${index}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base sm:text-lg font-bold text-[#344E41]">Lesson {index + 1}</h3>
                      {lesson.dailyActivities?.length > 0 && (
                        <span className="text-xs bg-[#D4A373]/20 text-[#D4A373] px-2 py-1 rounded-full">
                          {lesson.dailyActivities.length} activities
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleLessonExpand(index)}
                        className="text-[#588157] hover:bg-[#E9EDC9] p-2"
                      >
                        {lesson.expanded ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </Button>
                      {content.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeLesson(index)}
                          data-testid={`remove-lesson-${index}`}
                          className="text-[#588157] hover:bg-[#E9EDC9] p-2"
                        >
                          <X className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {lesson.expanded !== false && (
                    <>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-[#344E41]">Material Type *</Label>
                          <Select value={lesson.type} onValueChange={(value) => updateLesson(index, 'type', value)}>
                            <SelectTrigger data-testid={`lesson-type-${index}`} className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] rounded-lg">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Text Content</SelectItem>
                              <SelectItem value="video">Video (YouTube/Embedded)</SelectItem>
                              <SelectItem value="link">External Link/Resource</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-[#344E41]">Lesson Title *</Label>
                          <Input
                            value={lesson.title}
                            onChange={(e) => updateLesson(index, 'title', e.target.value)}
                            required
                            data-testid={`lesson-title-${index}`}
                            className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                          />
                        </div>

                        {lesson.type === 'text' && (
                          <div>
                            <Label className="text-[#344E41]">Content *</Label>
                            <Textarea
                              value={lesson.content}
                              onChange={(e) => updateLesson(index, 'content', e.target.value)}
                              required
                              rows={6}
                              data-testid={`lesson-content-${index}`}
                              className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                            />
                          </div>
                        )}

                        {(lesson.type === 'video' || lesson.type === 'link') && (
                          <div>
                            <Label className="text-[#344E41]">{lesson.type === 'video' ? 'Video URL *' : 'Link URL *'}</Label>
                            <Input
                              value={lesson.url}
                              onChange={(e) => updateLesson(index, 'url', e.target.value)}
                              required
                              placeholder={lesson.type === 'video' ? 'https://youtube.com/...' : 'https://...'}
                              data-testid={`lesson-url-${index}`}
                              className="bg-white/50 border-[#CCD5AE] focus:border-[#3A5A40] focus:ring-[#3A5A40]/20 rounded-lg"
                            />
                          </div>
                        )}
                      </div>

                      <ActivityEditor
                        lessonIndex={index}
                        lessonTitle={lesson.title}
                        lessonContent={lesson.type === 'text' ? lesson.content : lesson.title}
                        activities={lesson.dailyActivities || []}
                        setActivities={(acts) => updateLessonActivities(index, acts)}
                      />
                    </>
                  )}
                </Card>
              ))}
            </div>
          </Card>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              data-testid="cancel-button"
              className="flex-1 border-2 border-[#3A5A40] text-[#3A5A40] hover:bg-[#3A5A40] hover:text-white duration-300 rounded-lg order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="create-course-submit-button"
              className="flex-1 bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 hover:shadow-lg active:scale-95 font-medium rounded-lg py-4 sm:py-6 text-base sm:text-lg order-1 sm:order-2"
            >
              <Save className="w-4 h-4 sm:w-5 sm:h-5 mr-2" strokeWidth={1.5} />
              {loading ? 'Creating...' : 'Create Course'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}